<template>
    <data-card :remove-class="isViewForModal">
        <div class="row d-flex justify-content-between">
            <div :class="{ 'col-md-6': !isViewForModal }">
                <div class="card card-body bg-light mb-3">
                    <h3><i :class="checklistIcon"></i> <b>{{ translatedTaskTitle || task.title }}</b></h3>
                    <translate :translate-text="task.title" @translatedText="(translated) => translatedTaskTitle = translated" />
                    <h4><b>{{ $t('[[[Informacje podstawowe]]]') }}</b></h4>
                    <ideo-form-group :label="$t('[[[Nieruchomość]]]')">
                        <h5>{{ fullPropertyAddress }}</h5>
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Wydział]]]')">
                        <h5>{{ departmentName }}</h5>
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Opis zadania]]]')">
                        <h5>
                            <linkified v-if="translatedTaskDescription || task.description" :text="translatedTaskDescription || task.description" />
                            <template v-else>-</template>
                        </h5>
                        <translate v-if="task.description?.length" :translate-text="task.description" @translatedText="(translated) => translatedTaskDescription = translated" />
                    </ideo-form-group>
                    <ideo-form-group v-if="isTaskCleaning" :label="$t('[[[Dodane Koszty]]]')">
                        <div v-if="task.costs.length" class="d-flex">
                            <span v-for="(cost, index) in task.costs" :key="index" class="d-flex flex-column">
                                <ideo-button
                                    variant="primary"
                                    :to="{ name: 'renter-costs-edit', params: { publicId: cost.publicId }}"
                                >
                                    {{ cost.translatedName || cost.name }}
                                </ideo-button>
                                <translate :translate-text="cost.name" @translatedText="(translated) => cost.translatedName = translated" />
                            </span>
                        </div>
                        <h5 v-else>-</h5>
                    </ideo-form-group>
                    <div class="d-flex flex-column mb-2">
                        <span>{{ $t('[[[Status: ]]]') }}</span>
                        <span class="fw-bold">{{ status }}</span>
                    </div>
                    <ideo-form-group :label="$t('[[[Załączone zdjęcia]]]')" class="mb-0">
                        <fileupload v-model="task.photos" :max-files="50" :accept="['image/jpeg', 'image/png']" :disabled="true" :is-removable="false"></fileupload>
                    </ideo-form-group>
                    <div class="d-flex justify-content-center">
                        <ideo-button
                            v-if="task.publicId && !task.taskChats.length"
                            @click="$router.push({ name: 'chat-add-from-task', params: { taskPublicId: task.publicId } })"
                        >
                            {{ $t('[[[Stwórz czat]]]') }}
                        </ideo-button>
                        <ideo-button
                            v-if="task.taskChats.length"
                            @click="chatsVisible = !chatsVisible"
                        >
                            {{ chatsVisible ? $t('[[[Ukryj czat]]]') : $t('[[[Pokaż czat]]]') }}
                        </ideo-button>
                        <renter-photo-preview :photos="task.photos">
                            <ideo-button :disabled="!task.photos.length">{{ $t('[[[Zobacz zdjęcia]]]') }}</ideo-button>
                        </renter-photo-preview>
                    </div>
                    <div class="w-100">
                        <renter-chats-list :chats-visible="chatsVisible" :list="task.taskChats" />
                        <chat-view
                            v-if="currentChatPublicId"
                            :chat-public-id="currentChatPublicId"
                            @chatClosed="currentChatPublicId = ''"
                        />
                        <notes-from-task :parent-task-public-id="task.publicId" />
                    </div>
                </div>
            </div>
            <div :class="{ 'col-md-6': !isViewForModal }">
                <div class="card card-body bg-light mb-3">
                    <h4><b>{{ $t('[[[Informacje dodatkowe]]]') }}</b></h4>
                    <ideo-form-group :label="$t('[[[Przypisane do]]]')">
                        <h5 v-for="(assignedUser, index) in assignedUsers" :key="index"><i class="fas fa-user"></i> {{ assignedUser.givenName }} {{ assignedUser.surname }}</h5>
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Czas wykonania]]]')">
                        <h5>{{ timeTook }}</h5>
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Data wykonania]]]')">
                        <h5>{{ $filters.datetime(task.dateCompleted) }}</h5>
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Rezerwacja]]]')">
                        <template v-if="task.reservationPublicId">
                            <ideo-button v-if="task.reservationPublicId" :to="{ name: 'renter-reservations-details', params: { publicId: task.reservationPublicId }}">
                                {{ $t('[[[Przejdź do rezerwacji]]]') }}
                            </ideo-button>
                            <ideo-button v-if="task.reservationPublicId && clipboardAvailable() && reservationNumber" @click="copyToClipboard(reservationNumber)">{{ $t('[[[Kopiuj numer rezerwacji]]]') }}</ideo-button>
                        </template>
                        <h5 v-else>-</h5>
                    </ideo-form-group>
                    <!-- Recenzja gościa -->
                    <div v-if="task.guestReview" class="card mt-3 mb-3">
                        <div class="card-header d-flex align-items-center">
                            <i class="fas fa-star me-2 text-warning"></i>
                            <strong>{{ $t('[[[Recenzja gościa]]]') }}</strong>
                        </div>
                        <div class="card-body">
                            <div v-if="task.guestReview.reviewerName" class="mb-2">
                                <span class="text-muted small">{{ $t('[[[Gość]]]') }}: </span>
                                <span>{{ task.guestReview.reviewerName }}</span>
                            </div>
                            <div v-if="task.guestReview.submittedAtUtc" class="mb-2">
                                <span class="text-muted small">{{ $t('[[[Data recenzji]]]') }}: </span>
                                <span>{{ formatReviewDate(task.guestReview.submittedAtUtc) }}</span>
                            </div>
                            <div v-if="task.guestReview.channel" class="mb-2">
                                <span class="text-muted small">{{ $t('[[[Kanał]]]') }}: </span>
                                <span class="badge" :class="channelBadgeClass(task.guestReview.channel)">{{ channelLabel(task.guestReview.channel) }}</span>
                            </div>
                            <div v-if="task.guestReview.cleanlinessRating != null" class="mb-2">
                                <span class="text-muted small me-1">{{ $t('[[[Ocena czystości]]]') }}: </span>
                                <star-rating :value="task.guestReview.cleanlinessRating" :max="task.guestReview.maxRating" />
                            </div>
                            <div v-if="task.guestReview.overallRating != null" class="mb-2">
                                <span class="text-muted small me-1">{{ $t('[[[Ocena ogólna]]]') }}: </span>
                                <star-rating :value="task.guestReview.overallRating" :max="task.guestReview.maxRating" />
                            </div>
                            <div v-if="task.guestReview.publicReview" class="mt-2">
                                <span class="text-muted small d-block mb-1">{{ $t('[[[Treść recenzji]]]') }}:</span>
                                <p class="mb-0 fst-italic">{{ task.guestReview.publicReview }}</p>
                            </div>
                        </div>
                    </div>
                    <!-- End recenzja gościa -->
                    <ideo-form-group :label="$t('[[[Checklista]]]')" v-if="checklist">
                        <div>
                            <h5>{{ $t('[[[Nazwa:]]]') }} {{ checklist.name }}</h5>
                            <div v-for="(checklistItem, index) in checklist.checklistItems" :key="index" class="checklist-item">
                                <h6>{{ checklistItem.equipment.name }}</h6>
                                <action-button v-for="(task, taskIndex) in checklistItem.tasks" :key="taskIndex" variant="success" :text="task" icon="fa-regular fa-circle-check" />
                            </div>
                        </div>
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Komentarz Konserwatora]]]')" v-if="isTaskMaintanence">
                        <h5>{{ translatedMaintainerComment || task.maintainerComment || '-' }}</h5>
                        <translate v-if="task.maintainerComment?.length" :translate-text="task.maintainerComment" @translatedText="(translated) => translatedMaintainerComment = translated" />
                    </ideo-form-group>
                    <ideo-form-group v-if="task.completedByUserFullName" :label="$t('[[[Zakończone przez]]]')">
                        <h5><i class="fas fa-user"></i> {{ task.completedByUserFullName }}</h5>
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Notatka]]]')" v-if="task.comment">
                        <h5><linkified :text="translatedComment || task.comment" /></h5>
                        <translate v-if="task.comment?.length" :translate-text="task.comment" @translatedText="(translated) => translatedComment = translated" />
                    </ideo-form-group>
                </div>
            </div>
        </div>
        <action-button variant="primary" icon="far fa-edit" :text="$t('[[[Edycja]]]')" @click="$emit('forceEdit', true)" :disabled="cannotEditTask" />
        <template #footer v-if="!isViewForModal">
            <action-button variant="outline-dark" icon="far fa-arrow-alt-circle-left" :text="$t('[[[Powrót]]]')" @click="$router.back()" />
        </template>
        <ideo-button
            v-if="permissions.canViewChangeLog"
            @click="showTaskHistory"
            variant="secondary"
            class="w-100"
        >
            <i class="fas fa-history me-1"></i>
            {{ $t('[[[Historia zmian]]]') }}
        </ideo-button>
    </data-card>
</template>
<script lang="ts">
import { Prop, Watch } from '@/helpers/Decorators';
import { Vue, Options } from 'vue-class-component';
import TaskIcon from '@/modules/renter/components/elements/TaskIcon';
import TasksService, { FormModel as TaskModel, DepartmentName, AssigneeItemModel, DepartmentEnum, StatusName, StatusEnum, TaskMetaPermissions } from '@/modules/renter/tasks/services/TasksService';
import PropertiesService, { FormModel as PropertyModel } from '@/modules/renter/properties/services/PropertiesService';
import ChecklistsService, { FormModel as ChecklistModel } from '@/modules/renter/checklists/services/ChecklistsService';
import RenterPhotoPreview from '@/modules/renter/components/RenterPhotoPreview.vue';
import RenterChatsList from '@/modules/renter/chat/components/RenterChatsList.vue';
import ChatView from '@/modules/renter/chat/components/ChatView.vue';
import { StatusEnum as ChatStatusEnum } from '@/modules/renter/chat/services/ChatService';
import { getTimeFromOverallTime } from '@/modules/renter/tasks_management/helpers/timer';
import NotesFromTask from '@/modules/renter/notes/components/NotesFromTask.vue';
import { fetchReservation } from '../../common/helpers/autocomplete';
import StarRating from '@/modules/renter/salaries/components/StarRating.vue';

@Options({
    components: { RenterPhotoPreview, RenterChatsList, ChatView, NotesFromTask, StarRating },
    emits: ['forceEdit']
})
export default class TaskEnded extends Vue
{
    @Prop({ default: null }) public taskProp: TaskModel;
    @Prop({ default: '' }) public publicId: string;
    @Prop({ default: false }) public isViewForModal: boolean;
    @Prop() public taskPermissions: TaskMetaPermissions;

    public translatedTaskTitle: string = null;
    public translatedTaskDescription: string = null;
    public translatedMaintainerComment: string = null;
    public translatedComment: string = null;

    public task: TaskModel = null;
    public property: PropertyModel = null;
    public assignedUsers: AssigneeItemModel[] = [];
    public checklist: ChecklistModel = null;

    public permissions: TaskMetaPermissions = {
        canComplete: true,
        canDelete: true,
        canEdit: true,
        canEditFinishedTask: true,
        canEditPropActualWorkingTime: false,
        canViewChangeLog: true,
    };

    public reservationNumber: string = null;
    public currentChatPublicId: string = '';

    public statusArchived(status: string): boolean
    {
        return ChatStatusEnum[status] === ChatStatusEnum.Archival;
    }


    @Watch('task.reservationPublicId')
    private async onReservationIdChange(): Promise<void>
    {
        this.reservationNumber = null;

        if (!this.clipboardAvailable()) return;

        if (this.task.reservationPublicId)
        {
            const response = await fetchReservation(this.task.reservationPublicId);

            this.reservationNumber = response.avantioLocalizator;
        }
    }

    public async created(): Promise<void>
    {
        if (!this.taskProp && this.publicId)
        {
            const response = await TasksService.fetch(this.publicId);

            this.task = response.result;
            this.permissions = response.meta;
        }


        if (this.taskProp)
        {
            this.task = this.taskProp;
            this.permissions = this.taskPermissions;
        }

        const [ property, assignedUsers, checklist ] = await Promise.all([
            this.task.customPropertyPublicId ? await PropertiesService.fetchCustomProperty(this.task.customPropertyPublicId) : await PropertiesService.fetch(this.task.propertyPublicId),
            await TasksService.getAssignees(this.task.publicId),
            this.task.templateChecklistPublicId ? await ChecklistsService.fetchWithMatchedEquipment(this.task.templateChecklistPublicId, this.task.propertyPublicId) : { result: null }
        ]);

        this.property = property.result;
        this.assignedUsers = assignedUsers;
        this.checklist = checklist.result;
    }

    public get isTaskCleaning(): boolean
    {
        return this.task.department === DepartmentEnum[DepartmentEnum.Cleaning];
    }

    public get isTaskInspection(): boolean
    {
        return this.task.department === DepartmentEnum[DepartmentEnum.Inspection];
    }

    public get isTaskMaintanence(): boolean
    {
        return this.task.department === DepartmentEnum[DepartmentEnum.Maintenance];
    }

    public get isTaskCustomerService(): boolean
    {
        return this.task.department === DepartmentEnum[DepartmentEnum.CustomerService];
    }

    public get isTaskOwnerService(): boolean
    {
        return this.task.department === DepartmentEnum[DepartmentEnum.OwnerService];
    }

    public get checklistIcon(): string
    {
        return TaskIcon(this.task.department);
    }

    public get fullPropertyAddress(): string
    {
        if (!this.property || this.task.customPropertyPublicId) return '';

        if (this.task.customPropertyPublicId)
        {
            const { propertyName } = this.property;

            return propertyName;
        }

        const { streetName, streetNumber, door, city } = this.property;

        return `${streetName} ${door ? `${streetNumber}/${door}` : streetNumber}, ${city}`;
    }

    public get departmentName(): string
    {
        return DepartmentName(DepartmentEnum[this.task.department]);
    }

    public get timeTook(): string
    {
        return getTimeFromOverallTime(this.task.overallTime).string;
    }

    public get status(): string
    {
        return StatusName(StatusEnum[this.task.status]);
    }

    public get cannotEditTask(): boolean
    {
        return !this.permissions.canEditFinishedTask && !this.$auth.user().departments.includes(DepartmentEnum.MainCoordinator);
    }

    public chatsVisible: boolean = false;

    public showTaskHistory(): void
    {
        this.$router.push({
            name: 'renter-tasks-history',
            params: { publicId: this.task.publicId }
        });
    }

    public formatReviewDate(date: string | null): string
    {
        if (!date) return '';

        return new Date(date).toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    public channelLabel(channel: string | null): string
    {
        if (channel === 'airbnb2') return 'Airbnb';
        if (channel === 'bookingCom') return 'Booking.com';
        return channel ?? '-';
    }

    public channelBadgeClass(channel: string | null): string
    {
        if (channel === 'airbnb2') return 'bg-danger';
        if (channel === 'bookingCom') return 'bg-primary';
        return 'bg-secondary';
    }
}
</script>
<style scoped>
.d-flex{
    flex-wrap: wrap;
}
.d-flex button{
    margin: 5px 0;
}
h5{
    margin: 0;
}
div.checklist-item h6{
    margin-top: 10px;
    margin-left: 5px;
}

div.checklist-item button{
    margin-left: 10px;
}

</style>
